import { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import { ChevronDown, CheckCircle2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { usePlanContext } from "@/lib/usePlanContext"
import { useOnboardingChecklist } from "@/lib/useOnboardingChecklist"

export function OnboardingChecklist() {
  const { propertyId, isDemo } = usePropertyContext()
  const { status, isTrialing } = usePlanContext()
  const { items, completedCount, totalCount, isFullyComplete, isLoading } = useOnboardingChecklist(propertyId)
  const navigate = useNavigate()

  const hiddenKey = `stayframe_checklist_hidden_${propertyId}`
  const collapsedKey = `stayframe_checklist_collapsed_${propertyId}`

  const [isHidden, setIsHidden] = useState(() => {
    try { return localStorage.getItem(hiddenKey) === "true" } catch { return false }
  })
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try { return localStorage.getItem(collapsedKey) === "true" } catch { return false }
  })

  // Auto-collapse when 4+ items complete
  useEffect(() => {
    if (completedCount >= 4 && !isCollapsed) {
      setIsCollapsed(true)
      try { localStorage.setItem(collapsedKey, "true") } catch {}
    }
  }, [completedCount, collapsedKey, isCollapsed])

  const handleHeaderClick = () => {
    if (isFullyComplete) {
      setIsHidden(true)
      try { localStorage.setItem(hiddenKey, "true") } catch {}
      return
    }
    const next = !isCollapsed
    setIsCollapsed(next)
    try { localStorage.setItem(collapsedKey, String(next)) } catch {}
  }

  // Visibility rules
  if (isDemo) return null
  if (status === "active" && !isTrialing) return null
  if (isHidden) return null
  if (isLoading) return null

  const progress = (completedCount / totalCount) * 100

  return (
    <div className="mx-3 mb-3">
      <div className="bg-card/60 backdrop-blur-sm border border-border/40 rounded-lg overflow-hidden">
        {/* Header — always visible */}
        <button
          type="button"
          className="w-full flex items-center justify-between px-3 py-2.5 cursor-pointer bg-transparent border-0 hover:bg-sidebar-accent/30 transition-colors"
          onClick={handleHeaderClick}
        >
          <span className="text-xs font-semibold tracking-wide text-foreground">
            Get Started
          </span>
          <span className="flex items-center gap-1.5">
            {isFullyComplete ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
            ) : (
              <span className="text-[11px] text-muted-foreground">
                {completedCount}/{totalCount}
              </span>
            )}
            <ChevronDown className={cn(
              "h-3 w-3 text-muted-foreground transition-transform duration-200",
              isCollapsed && "-rotate-90"
            )} />
          </span>
        </button>

        {/* Progress bar — always visible */}
        <div className="h-0.5 w-full bg-muted">
          <div
            className="h-full bg-teal-600 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Items — collapsible */}
        {!isCollapsed && (
          <div className="py-1">
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-1.5 text-xs bg-transparent border-0 cursor-pointer transition-colors",
                  item.completed
                    ? "text-muted-foreground/60"
                    : "text-sidebar-foreground hover:bg-sidebar-accent/30"
                )}
                onClick={() => {
                  if (item.linkTo.startsWith("/")) {
                    navigate(item.linkTo)
                  } else {
                    navigate(item.linkTo, { relative: "path" })
                  }
                }}
              >
                {item.completed ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                ) : (
                  <item.icon className="h-3.5 w-3.5 text-muted-foreground/50 flex-shrink-0" />
                )}
                <span className={cn(item.completed && "line-through")}>
                  {item.label}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
